import { authApi } from '../api/modules/auth.api';
import { clearAuthStorage, getApiErrorMessage, setTokens } from '../api/client';
import { TOKEN_KEYS, USE_API } from '../config/api';
import { staffFromApi } from '../models';
import { allPermissions } from '../data/staff/permissions';
import { getStaffUsers } from '../data/staff/staffStore';

function persistSession({ accessToken, refreshToken, staff, permissions, isSuperAdmin }) {
  setTokens(accessToken, refreshToken);
  if (staff) localStorage.setItem(TOKEN_KEYS.staff, JSON.stringify(staff));
  if (permissions) {
    localStorage.setItem(TOKEN_KEYS.permissions, JSON.stringify(permissions));
  }
  localStorage.setItem(TOKEN_KEYS.isSuperAdmin, String(Boolean(isSuperAdmin)));
  // Legacy key used by older AuthContext
  localStorage.setItem('utkarsh_admin_token', accessToken || '');
}

function mockLogin(email) {
  const users = getStaffUsers();
  const match =
    users.find((u) => u.email?.toLowerCase() === String(email || '').toLowerCase()) ||
    users.find((u) => u.active) ||
    users[0];
  const staff = match || {
    staffId: 'STF-MOCK',
    fullName: 'Mock Admin',
    email: email || 'admin@utkarsh.com',
    mobile: '',
    roleIds: ['ROLE-SUPER'],
    scopeType: 'all',
    buId: '',
    siteId: '',
    active: true,
  };
  const accessToken = 'mock-access-token';
  const refreshToken = 'mock-refresh-token';
  const permissions = allPermissions();
  persistSession({
    accessToken,
    refreshToken,
    staff,
    permissions,
    isSuperAdmin: true,
  });
  return {
    accessToken,
    refreshToken,
    staff,
    permissions,
    isSuperAdmin: true,
  };
}

export async function login(email, password) {
  if (!USE_API) return mockLogin(email);
  try {
    const data = await authApi.login(email, password);
    const staff = staffFromApi(data.staff) || data.staff;
    persistSession({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      staff,
      permissions: data.permissions || {},
      isSuperAdmin: Boolean(data.isSuperAdmin),
    });
    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      staff,
      permissions: data.permissions || {},
      isSuperAdmin: Boolean(data.isSuperAdmin),
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Login failed'));
  }
}

export async function loadMe() {
  if (!USE_API) {
    const raw = localStorage.getItem(TOKEN_KEYS.staff);
    const permissionsRaw = localStorage.getItem(TOKEN_KEYS.permissions);
    return {
      staff: raw ? JSON.parse(raw) : mockLogin().staff,
      permissions: permissionsRaw ? JSON.parse(permissionsRaw) : allPermissions(),
      isSuperAdmin: localStorage.getItem(TOKEN_KEYS.isSuperAdmin) !== 'false',
    };
  }
  try {
    const data = await authApi.me();
    const staff = staffFromApi(data.staff) || data.staff;
    if (staff) localStorage.setItem(TOKEN_KEYS.staff, JSON.stringify(staff));
    if (data.permissions) {
      localStorage.setItem(TOKEN_KEYS.permissions, JSON.stringify(data.permissions));
    }
    localStorage.setItem(TOKEN_KEYS.isSuperAdmin, String(Boolean(data.isSuperAdmin)));
    return {
      staff,
      permissions: data.permissions || {},
      isSuperAdmin: Boolean(data.isSuperAdmin),
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load profile'));
  }
}

export async function logout() {
  const refreshToken = localStorage.getItem(TOKEN_KEYS.refresh);
  if (USE_API && refreshToken) {
    try {
      await authApi.logout(refreshToken);
    } catch {
      // ignore logout API errors
    }
  }
  clearAuthStorage();
}

export function readStoredSession() {
  const accessToken = localStorage.getItem(TOKEN_KEYS.access);
  const refreshToken = localStorage.getItem(TOKEN_KEYS.refresh);
  let staff = null;
  let permissions = null;
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.staff);
    if (raw) staff = JSON.parse(raw);
  } catch {
    staff = null;
  }
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.permissions);
    if (raw) permissions = JSON.parse(raw);
  } catch {
    permissions = null;
  }
  const isSuperAdmin = localStorage.getItem(TOKEN_KEYS.isSuperAdmin) === 'true';
  return { accessToken, refreshToken, staff, permissions, isSuperAdmin };
}
